// apps/console/src/components/organisms/AlertInbox/AlertSnapshot.tsx
//
// Sanitized thumbnail for a field-device alert snapshot (face-rec C2). The alert carries an OPAQUE
// gisify media key (e.g. `robots/central/snapshots/<uuid>.jpg`), NOT a URL — a header-less <img>
// can't carry the operator's JWT, so what renders is a short-lived presigned url for the key.
//
// On mount the key is looked up through `resolveMediaUrl`. For a photo the alert log lists, the
// link is normally already cached: the log asks `GET /robots/alerts` to sign its photos
// (`include=mediaUrls`) and caches them before its rows render, so the log costs no resolve request.
// Anything else — a photo past the list's cap, a live-pushed alert, the emergency card, a lapsed
// link — goes to the batched resolver: a snapshot key once whatever robotId each viewer passes, a
// helmet key once per device.
//
// This REPLACES the previous unguarded `<img src={alert.snapshotRef}>` at the AlertRow. The
// client-side shape check (`classifyMediaRef`) is a first-line sanitizer that rejects
// `javascript:` / `data:` / absolute URLs / other shapes so a compromised device payload can never
// be injected as an <img src>; the backend regex + tenant gate is the authoritative guard.
//
// WHICH resolver a key goes to is decided by its FOLDER, in `robot-media-ref`, not assumed here. A
// helmet's alarm photo lives under `helmets/…` and the snapshot resolver refuses it; while this
// component carried its own `robots/`-only prefilter, a helmet alarm simply never showed its
// picture — the one frame from the moment the alarm fired.

import { ImageOff } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  __resetMediaRefCacheForTests,
  classifyMediaRef,
  evictMediaRef,
  resolveMediaUrl,
} from '../RobotMedia/robot-media-ref'

export interface AlertSnapshotProps {
  /** The opaque media key from `alert.snapshotRef`. */
  refKey: string | undefined
  alt: string
  className?: string
  /** The device the alert came from, which narrows a helmet resolve to its own captures. */
  robotId?: string
}

export function AlertSnapshot({ refKey, alt, className, robotId }: AlertSnapshotProps) {
  const [url, setUrl] = useState<string | null>(null)
  // Bumped by the <img> onError self-heal to force a re-resolve after evicting.
  const [refreshNonce, setRefreshNonce] = useState(0)
  // Guard: at most one self-heal per successful display (re-armed on load) so a
  // genuinely-broken ref can't loop forever.
  const healedRef = useRef(false)
  // The fresh link failed too. Usually the photo was deleted (the alarm still names it), but a
  // network blip looks the same from an <img>, so this says the picture could not be loaded rather
  // than hiding it: a picture that exists is not silently dropped, and nothing shows a broken image.
  const [gone, setGone] = useState(false)
  const { t } = useTranslation('console')

  // Re-arm the self-heal guard only when the ref itself changes (NOT on a
  // refreshNonce bump — that would defeat the loop guard).
  useEffect(() => {
    healedRef.current = false
    setGone(false)
  }, [refKey])

  useEffect(() => {
    setUrl(null)

    if (!refKey || classifyMediaRef(refKey) === null) return

    let cancelled = false

    void resolveMediaUrl(refKey, robotId).then(resolved => {
      if (!cancelled) setUrl(resolved)
    })

    return () => {
      cancelled = true
    }
  }, [refKey, robotId, refreshNonce])

  if (gone) {
    const label = t('alerts.log.pictureUnavailable', "Couldn't load this picture")

    return (
      <span
        role="img"
        aria-label={label}
        title={label}
        className={`flex items-center justify-center bg-gray-100 text-gray-400 dark:bg-white/5 dark:text-gray-500 ${className ?? ''}`}
      >
        <ImageOff className="h-5 w-5" aria-hidden />
      </span>
    )
  }

  if (!url) return null

  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      className={className}
      onLoad={() => {
        // A fresh successful display re-arms one future self-heal.
        healedRef.current = false
      }}
      onError={() => {
        // A displayed presigned URL can lapse (S3 "Request has expired"). Evict the
        // stale cache entry and re-resolve a fresh url ONCE per successful display.
        if (refKey && !healedRef.current) {
          healedRef.current = true
          evictMediaRef(refKey, robotId)
          setRefreshNonce(n => n + 1)
        } else {
          setGone(true)
        }
      }}
    />
  )
}

/** Test-only: clear the module-level resolve cache between specs. */
export function __resetAlertSnapshotCacheForTests(): void {
  __resetMediaRefCacheForTests()
}
